import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedDocument } from '@asset-metrix/portal-gateway-client-js';
import clsx from 'clsx';

import { ButtonProps } from '../../buttons/Button';
import { DocumentListing } from '../../document-listing/DocumentListing';
import { GoogleFontIcon } from '../../icons/GoogleFontIcon';
import { SVGIcon } from '../../icons/SVGIcon';
import { InlineAlert } from '../../inline-alert/InlineAlert';
import { ActionButtons } from '../TableSidebarWidget';

import styles from './related-documents.module.scss';

export type RelatedDocumentsProps = {
  documents?: RelatedDocument[];
  error?: Error | null;
  loading?: boolean;
  onDocumentClick?: (document: RelatedDocument) => void;
  onDocumentDownload?: () => void;
  onOpenInPage?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  openInPageButtonText?: string;
  selectedDocument?: RelatedDocument;
  selectedDocumentPreviewUrl?: string;
};

export const RelatedDocuments = ({
  documents,
  loading,
  onDocumentDownload,
  onDocumentClick,
  onOpenInPage,
  openInPageButtonText,
  selectedDocument,
  selectedDocumentPreviewUrl,
  error,
}: RelatedDocumentsProps) => {
  const { t } = useTranslation();

  const actionButtons: ButtonProps[] = useMemo(() => {
    const buttons: ButtonProps[] = [];
    if (onOpenInPage) {
      buttons.push({
        variant: 'secondary',
        icon: <GoogleFontIcon name="open_in_new" />,
        onClick: onOpenInPage,
        title: openInPageButtonText ?? t('app.tableSidebarWidget.openInPage'),
        disabled: !documents || documents.length === 0,
      });
    }
    if (onDocumentDownload) {
      buttons.push({
        variant: 'primary',
        icon: <SVGIcon name="download" />,
        onClick: onDocumentDownload,
        title: t('app.tableSidebarWidget.download'),
        disabled: !selectedDocument || selectedDocument?.virusFree === false,
      });
    }
    return buttons;
  }, [
    onDocumentDownload,
    onOpenInPage,
    openInPageButtonText,
    selectedDocument,
    t,
    documents,
  ]);

  return (
    <div className={styles['root']} data-testid="related-documents">
      <div className={styles['document-listing-container']}>
        {error ? (
          <InlineAlert className={styles['alert']}>
            <p>
              {t('app.tableSidebarWidget.errorText')}
              <br />
              {error.message}
            </p>
          </InlineAlert>
        ) : (
          <>
            <DocumentListing
              className={clsx(
                selectedDocumentPreviewUrl && styles['show-preview']
              )}
              documents={documents}
              loading={loading}
              onDocumentClick={onDocumentClick}
              selectedDocument={selectedDocument}
            />
            {selectedDocumentPreviewUrl && (
              <iframe
                // see for more info about the parameters: https://pdfobject.com/pdf/pdf_open_parameters_acro8.pdf
                src={`${selectedDocumentPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0`}
                style={{ width: '100%', height: '60%' }}
                title={`${selectedDocument?.fileName} - PDF Viewer`}
              />
            )}
          </>
        )}
      </div>
      <ActionButtons buttons={actionButtons} />
    </div>
  );
};
